import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import {
  Activity, RefreshCw, AlertTriangle, TrendingUp, Users, Target,
  BarChart3, Stethoscope, Heart, Brain
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const PatientAcuityDashboard = () => {
  const [dashboardData, setDashboardData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('dashboard');

  useEffect(() => {
    fetchDashboard();
  }, []);

  const fetchDashboard = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/phase63/dashboard`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        setDashboardData(await response.json());
      }
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  const getAcuityBadge = (level) => {
    const config = {
      critical: 'bg-red-600 text-white',
      high: 'bg-orange-500 text-white',
      moderate: 'bg-yellow-500 text-black',
      low: 'bg-green-500 text-white'
    };
    return <Badge className={config[level] || config.moderate}>{level.toUpperCase()}</Badge>;
  };

  const StatCard = ({ title, value, subtitle, icon: Icon, color = 'blue' }) => (
    <Card>
      <CardContent className="p-4">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm text-slate-600 font-medium">{title}</p>
            <p className="text-2xl font-bold text-slate-900 mt-1">{value}</p>
            {subtitle && <p className="text-xs text-slate-500 mt-1 font-medium">{subtitle}</p>}
          </div>
          <div className={`p-3 rounded-full bg-${color}-100`}>
            <Icon className={`h-6 w-6 text-${color}-600`} />
          </div>
        </div>
      </CardContent>
    </Card>
  );

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <RefreshCw className="h-8 w-8 animate-spin text-teal-600" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50" data-testid="patient-acuity-dashboard">
      {/* Header */}
      <div className="bg-white border-b sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-slate-900">Patient Acuity Scoring</h1>
              <p className="text-sm text-slate-600 mt-1 font-medium">AI-powered care level recommendations</p>
            </div>
            <Button variant="outline" onClick={fetchDashboard}>
              <RefreshCw className="h-4 w-4 mr-2" />
              Refresh
            </Button>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="mb-6">
            <TabsTrigger value="dashboard" className="font-semibold">Dashboard</TabsTrigger>
            <TabsTrigger value="patients" className="font-semibold">Patient Scores</TabsTrigger>
            <TabsTrigger value="analytics" className="font-semibold">Analytics</TabsTrigger>
          </TabsList>

          <TabsContent value="dashboard" className="space-y-6">
            {/* Stats */}
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
              <StatCard title="Total Patients" value={dashboardData?.stats?.total_patients || 0} icon={Users} color="blue" />
              <StatCard title="Critical" value={dashboardData?.stats?.critical_count || 0} icon={AlertTriangle} color="red" />
              <StatCard title="High Acuity" value={dashboardData?.stats?.high_count || 0} icon={Activity} color="orange" />
              <StatCard title="Avg Score" value={dashboardData?.stats?.avg_score || 0} icon={Target} color="purple" />
              <StatCard title="AI Accuracy" value={`${dashboardData?.stats?.ai_accuracy || 0}%`} icon={Brain} color="teal" />
            </div>

            {/* Acuity Distribution */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <Card>
                <CardHeader>
                  <CardTitle className="text-slate-900 font-bold">Acuity Distribution</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    {[
                      { level: 'critical', color: 'bg-red-600', count: dashboardData?.distribution?.critical || 0 },
                      { level: 'high', color: 'bg-orange-500', count: dashboardData?.distribution?.high || 0 },
                      { level: 'moderate', color: 'bg-yellow-500', count: dashboardData?.distribution?.moderate || 0 },
                      { level: 'low', color: 'bg-green-500', count: dashboardData?.distribution?.low || 0 }
                    ].map((item) => (
                      <div key={item.level} className="flex items-center gap-4">
                        <div className="w-20">
                          <span className="text-sm font-semibold text-slate-900 capitalize">{item.level}</span>
                        </div>
                        <div className="flex-1 bg-gray-200 rounded-full h-4">
                          <div className={`${item.color} h-4 rounded-full`} style={{ width: `${(item.count / (dashboardData?.stats?.total_patients || 1)) * 100}%` }} />
                        </div>
                        <span className="font-bold text-slate-900 w-12 text-right">{item.count}</span>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>

              {/* High Priority Patients */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-slate-900 font-bold">High Priority Patients</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {dashboardData?.high_priority_patients?.slice(0, 5).map((patient) => (
                      <div key={patient.id} className="flex items-center justify-between p-3 bg-red-50 rounded-lg border border-red-100">
                        <div>
                          <p className="font-semibold text-slate-900">{patient.name}</p>
                          <p className="text-sm text-slate-600">{patient.primary_diagnosis}</p>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="font-bold text-slate-900">{patient.acuity_score}</span>
                          {getAcuityBadge(patient.acuity_level)}
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Scoring Factors */}
            <Card>
              <CardHeader>
                <CardTitle className="text-slate-900 font-bold">Acuity Scoring Factors</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  {[
                    { factor: 'Symptom Burden', icon: Activity, weight: '25%' },
                    { factor: 'Functional Status', icon: Users, weight: '20%' },
                    { factor: 'Disease Trajectory', icon: TrendingUp, weight: '20%' },
                    { factor: 'Care Complexity', icon: Stethoscope, weight: '15%' },
                    { factor: 'Psychosocial Needs', icon: Heart, weight: '10%' },
                    { factor: 'Family Support', icon: Users, weight: '5%' },
                    { factor: 'Medication Complexity', icon: Activity, weight: '5%' }
                  ].map((item) => (
                    <div key={item.factor} className="p-4 bg-gray-50 rounded-lg text-center">
                      <item.icon className="h-8 w-8 text-teal-600 mx-auto mb-2" />
                      <p className="font-semibold text-slate-900 text-sm">{item.factor}</p>
                      <Badge variant="outline" className="mt-2">{item.weight}</Badge>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="patients" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="text-slate-900 font-bold">All Patient Acuity Scores</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b text-left">
                        <th className="px-4 py-3 text-sm font-bold text-slate-900">Patient</th>
                        <th className="px-4 py-3 text-sm font-bold text-slate-900">Diagnosis</th>
                        <th className="px-4 py-3 text-sm font-bold text-slate-900">Score</th>
                        <th className="px-4 py-3 text-sm font-bold text-slate-900">Level</th>
                        <th className="px-4 py-3 text-sm font-bold text-slate-900">Trend</th>
                        <th className="px-4 py-3 text-sm font-bold text-slate-900">Last Updated</th>
                      </tr>
                    </thead>
                    <tbody>
                      {dashboardData?.patients?.map((patient) => (
                        <tr key={patient.id} className="border-b hover:bg-gray-50">
                          <td className="px-4 py-3 font-semibold text-slate-900">{patient.name}</td>
                          <td className="px-4 py-3 text-slate-600">{patient.primary_diagnosis}</td>
                          <td className="px-4 py-3 font-bold text-slate-900">{patient.acuity_score}</td>
                          <td className="px-4 py-3">{getAcuityBadge(patient.acuity_level)}</td>
                          <td className="px-4 py-3">
                            <Badge className={patient.trend === 'up' ? 'bg-red-500' : patient.trend === 'down' ? 'bg-green-500' : 'bg-gray-500'}>
                              {patient.trend === 'up' ? '↑' : patient.trend === 'down' ? '↓' : '→'} {patient.trend}
                            </Badge>
                          </td>
                          <td className="px-4 py-3 text-sm text-slate-600">{new Date(patient.last_updated).toLocaleDateString()}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="analytics" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="text-slate-900 font-bold">Acuity Analytics</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  <div className="p-4 bg-gray-50 rounded-lg">
                    <h4 className="font-bold text-slate-900 mb-2">Score Distribution</h4>
                    <p className="text-3xl font-bold text-teal-600">{dashboardData?.stats?.avg_score || 0}</p>
                    <p className="text-sm text-slate-600">Average acuity score</p>
                  </div>
                  <div className="p-4 bg-gray-50 rounded-lg">
                    <h4 className="font-bold text-slate-900 mb-2">Trending Up</h4>
                    <p className="text-3xl font-bold text-red-600">{dashboardData?.analytics?.trending_up || 0}</p>
                    <p className="text-sm text-slate-600">Patients with increasing acuity</p>
                  </div>
                  <div className="p-4 bg-gray-50 rounded-lg">
                    <h4 className="font-bold text-slate-900 mb-2">Care Adjustments</h4>
                    <p className="text-3xl font-bold text-blue-600">{dashboardData?.analytics?.care_adjustments || 0}</p>
                    <p className="text-sm text-slate-600">Recommended this week</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default PatientAcuityDashboard;
